import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { iniciales, nombreUsuario, rolUsuario } from './usuario-display';
import { NavIconComponent } from './nav-icon';
import { NavSection, SECCIONES } from './navegacion';
import { LayoutService } from '../services/layout.service';
import { PermissionsService } from '../services/permissions.service';
import { VersionService } from '../services/version.service';

/**
 * Preferencias de menú que se guardaron mientras el estado se recordaba entre
 * visitas. Ya no se leen —el menú arranca siempre plegado—, así que se borran
 * para no dejar basura en el navegador de quien las tenga.
 */
const CLAVES_ANTIGUAS = [
  'silabvet-menu-secciones-abiertas',
  'silabvet-menu-secciones-cerradas',
];

@Component({
  selector: 'app-sidebar',
  imports: [NavIconComponent, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.html',
})
export class Sidebar {
  protected readonly layout = inject(LayoutService);
  private readonly permissions = inject(PermissionsService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  /**
   * Posición y ancho del `<aside>`. Van juntos en un computed porque en móvil
   * el cajón entra y sale con translate, y solo desde `lg` el ancho depende de
   * si está colapsado.
   */
  protected readonly clasesAside = computed(() =>
    [
      this.layout.sidebarMovilAbierto() ? 'translate-x-0' : '-translate-x-full',
      this.layout.sidebarCompacto() ? 'lg:w-sidebar-compacto' : 'lg:w-sidebar',
    ].join(' '),
  );

  /** Versión desplegada; el detalle completo está en el modal de perfil. */
  protected readonly version = inject(VersionService).etiqueta;

  protected readonly nombre = computed(() => nombreUsuario(this.auth.usuario()));
  protected readonly iniciales = computed(() => iniciales(this.auth.usuario()));
  protected readonly rol = computed(() => rolUsuario(this.auth.usuario()));

  /** Solo las opciones permitidas; las secciones que quedan vacías se ocultan. */
  protected readonly secciones = computed<NavSection[]>(() =>
    SECCIONES.map((seccion) => ({
      ...seccion,
      items: seccion.items.filter((item) => this.permissions.can(item.permiso)),
    })).filter((seccion) => seccion.items.length > 0),
  );

  /**
   * Secciones desplegadas. Arranca vacío a propósito: el menú se abre siempre
   * plegado y lo que el usuario despliegue dura lo que dure la sesión de la
   * pestaña, no se recuerda entre visitas.
   */
  private readonly abiertas = signal<ReadonlySet<string>>(new Set());

  /** Ya se abrió la sección de la pantalla con la que se entró a la app. */
  private aperturaInicialHecha = false;

  /** Ruta actual; con ella se marca la sección de la pantalla abierta. */
  private readonly url = signal(this.router.url);

  constructor() {
    for (const clave of CLAVES_ANTIGUAS) {
      localStorage.removeItem(clave);
    }

    // Al entrar por una URL directa, la sección de esa pantalla se despliega
    // aunque estuviera plegada; después manda el usuario y ya no se reabre
    // sola en cada navegación.
    this.router.events.pipe(takeUntilDestroyed()).subscribe((evento) => {
      if (!(evento instanceof NavigationEnd)) {
        return;
      }
      this.url.set(evento.urlAfterRedirects);
      if (!this.aperturaInicialHecha) {
        this.aperturaInicialHecha = true;
        const activa = this.secciones().find((seccion) => this.seccionActiva(seccion));
        if (activa) {
          this.abrirSeccion(activa.label);
        }
      }
    });
  }

  /**
   * Una sección con una sola opción se dibuja como enlace directo: plegar un
   * grupo de un solo elemento no ahorra nada y esconde la pantalla.
   *
   * Se exceptúan las que se declaran con `siempreGrupo`, que son secciones
   * recién empezadas: ahí el grupo sí aporta, porque muestra a qué sección
   * pertenece la pantalla y no cambia de forma cuando llegue la segunda hoja.
   */
  protected esDirecta(seccion: NavSection): boolean {
    return seccion.items.length === 1 && !seccion.siempreGrupo;
  }

  /**
   * La sección a la que pertenece la pantalla abierta. Se resalta aunque esté
   * plegada, para no perder de vista dónde se está parado.
   */
  protected seccionActiva(seccion: NavSection): boolean {
    return seccion.items.some((item) => this.url().startsWith(item.route));
  }

  /** Una sección se dibuja plegada mientras el usuario no la despliegue. */
  protected abierta(label: string): boolean {
    return this.abiertas().has(label);
  }

  protected alternarSeccion(label: string): void {
    this.abiertas.update((abiertas) => {
      const siguiente = new Set(abiertas);
      if (!siguiente.delete(label)) {
        siguiente.add(label);
      }
      return siguiente;
    });
  }

  private abrirSeccion(label: string): void {
    if (!this.abiertas().has(label)) {
      this.abiertas.update((abiertas) => new Set(abiertas).add(label));
    }
  }
}
